import { boolean, InferType, number, object, string } from 'yup';
import { includes } from 'lodash/fp';
import { INCOME_TIER, INCOME_TYPE, PAYMENT_FREQUENCY, STATE } from '@ahmdigital/constants';
import { messageDefaults } from '@ahmdigital/form-validation';

import { AGE_FIELD_ERROR_MESSAGES, MAX_AGE, MIN_AGE } from '../../../pages/home/data/constants';
import { EXCESS_TYPES } from '../../../ahm-constants/ui';
import { INCOME_TIER_NONE } from '../../../components/pricing-drawer/constants';
import { SCALE_OPTIONS, SCALES_WITH_PARTNER } from '../../../models/customer/constants';

const { integer, minAge, minAgePartner, required, validAge } = messageDefaults;

export const INCOME_TIER_MESSAGE = 'Select annual income';
const incomeTierValues = [...Object.values(INCOME_TIER), INCOME_TIER_NONE] as string[];

const incomeTypeValues = Object.values(INCOME_TYPE) as string[];
const INCOME_TYPE_MESSAGE = 'Select income type';

const stateValues = Object.values(STATE) as string[];
export const STATE_MESSAGE = 'Select state';

export const SCALE_MESSAGE = 'Select cover type';

const frequencyValues = Object.values(PAYMENT_FREQUENCY) as string[];
export const FREQUENCY_MESSAGE = 'Select payment frequency';

const hospitalExcessValues = EXCESS_TYPES.map((excess) => excess.toString());
const HOSPITAL_EXCESS_MESSAGE = 'Select hospital excess';

// Empty fields on query params are passed as an empty string, causing a misleading error message to be displayed,
// ie. showing "Numbers only please" instead of "Required".
const convertEmptyStringToUndefined = (value: string | undefined, originalValue: string) =>
  originalValue === '' ? undefined : value;

/**
 * Shared refine-price schema: canonical pricing domain validation contract.
 */
export const refinePriceSchema = object({
  age: number()
    .transform(convertEmptyStringToUndefined)
    .typeError(integer)
    .required(AGE_FIELD_ERROR_MESSAGES.AGE)
    .positive()
    .integer(integer)
    .min(MIN_AGE, minAge)
    .max(MAX_AGE, validAge),
  frequency: string().required(FREQUENCY_MESSAGE).oneOf(frequencyValues, FREQUENCY_MESSAGE),
  hospitalExcess: string().required(HOSPITAL_EXCESS_MESSAGE).oneOf(hospitalExcessValues, HOSPITAL_EXCESS_MESSAGE),
  incomeTier: string().required(INCOME_TIER_MESSAGE).oneOf(incomeTierValues, INCOME_TIER_MESSAGE),
  incomeType: string().required(INCOME_TYPE_MESSAGE).oneOf(incomeTypeValues, INCOME_TYPE_MESSAGE),
  partnerAge: number()
    .transform(convertEmptyStringToUndefined)
    .typeError(integer)
    .nullable()
    .when('scale', {
      is: (value: string) => includes(value, SCALES_WITH_PARTNER),
      then: (rule) =>
        rule
          .required(AGE_FIELD_ERROR_MESSAGES.PARTNER_AGE)
          .positive()
          .integer(integer)
          .min(MIN_AGE, minAgePartner)
          .max(MAX_AGE, validAge),
    }),
  rebate: boolean().required(required),
  scale: string().required(SCALE_MESSAGE).oneOf(SCALE_OPTIONS, SCALE_MESSAGE),
  state: string().required(STATE_MESSAGE).oneOf(stateValues, STATE_MESSAGE),
});

export type RefinePriceSchemaType = InferType<typeof refinePriceSchema>;
